import { Injectable, inject } from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {
  ForgetPassword,
  ForgetPasswordError,
  ForgetPasswordSuccess,
  CompleteLoginFlow,
  LoginError,
  LoginFlowRequired,
  LoginSuccess,
  LoginUser,
  LoginUserWithId, LoginUserWithIdError, LoginUserWithIdSuccess,
  EndImpersonation, EndImpersonationError,
  LogoutUser, LogoutSuccess,
  ResetPassword,
  ResetPasswordError,
  ResetPasswordSuccess, ResetPasswordToken, ResetPasswordTokenSuccess,
  TokenLogin,
  OneClickLogin,
  ResetAuthState
} from '@app/stores/authentication/authentication.actions';
import {IAuthenticationState} from '@app/stores/authentication/authentication.state';
import {catchError, map, switchMap} from 'rxjs/operators';
import {of} from 'rxjs';
import {AuthenticationService} from '@app/services/authentication.service';
import {LoaderService} from '@app/services/loader.service';
import {User} from '@app/modules/shared/models';

@Injectable()
export class AuthenticationEffects {
  private _actions$ = inject(Actions);
  private _store = inject<Store<IAuthenticationState>>(Store);
  private authenticationService = inject(AuthenticationService);
  private loaderService = inject(LoaderService);


  /** Delegates to AuthenticationService so the interceptor cannot drift from it. */
  private buildUserData(response: any): any {
    return this.authenticationService.buildUserData(response);
  }

  private loginSuccess(response: any, impersonation = false): any {
    const userData = this.buildUserData(response);
    const user = Object.assign(new User(), userData);
    this.authenticationService.setAccessToken(response.token);
    this.authenticationService.currentUser.next(user);
    const storedUser = {...userData};
    delete storedUser.token;
    localStorage.removeItem('reportPassword');
    localStorage.removeItem('resetUser');
    localStorage.setItem('currentUser', JSON.stringify(storedUser));
    this.loaderService.hide();
    const impersonating = !!response?.impersonation?.active;
    return impersonation || impersonating
      ? LoginUserWithIdSuccess({user})
      : LoginSuccess({user, impersonating});
  }

  private loginFailure(response: any): any {
    let flow = response?.error || response;
    if (flow?.code === 'AUTH_INVALID_CREDENTIALS' && flow.captcha_required) {
      flow = {...flow, code: 'AUTH_CAPTCHA_REQUIRED'};
    }
    if (flow?.code?.startsWith('AUTH_') && flow.code !== 'AUTH_INVALID_CREDENTIALS') {
      if (flow.token) {
        this.authenticationService.setAccessToken(flow.token);
      }
      this.loaderService.hide();
      return LoginFlowRequired({flow});
    }
    this.loaderService.hide(true);
    return LoginError({error: this.loaderService.getErrorMessage(response, 'Invalid credentials')});
  }

  private clearLocalAuthentication(): void {
    this.authenticationService.setAccessToken(null);
    this.authenticationService.currentUser.next(null);
    localStorage.removeItem('currentUser');
    localStorage.removeItem('admin_currentUser');
    localStorage.removeItem('reportPassword');
    localStorage.removeItem('resetUser');
    localStorage.removeItem('skip');
    localStorage.removeItem('projectFilterList');
    localStorage.removeItem('influencerSearchValue');
    this.loaderService.unsubscribe();
  }

  getLogin$ = createEffect(() => this._actions$.pipe(
    ofType(LoginUser),
    switchMap((action) => {
      this.loaderService.show();
      return this.authenticationService.login(
        action.credentials.email,
        action.credentials.password,
        action.credentials.remember,
        action.credentials.turnstileToken
      ).pipe(
        map((response: any) => {
          if (response.success) {
            return this.loginSuccess(response);
          }
          return this.loginFailure(response);
        }),
        catchError((error) => of(this.loginFailure(error)))
      );
    })
  ));

  userLogout$ = createEffect(() =>  this._actions$.pipe(
    ofType(LogoutUser),
    switchMap(() => {
      this.loaderService.show();
      if (localStorage.getItem('currentUser')) {
        return this.authenticationService.logout().pipe(
          map(() => {
            this.clearLocalAuthentication();
            this.loaderService.hide();
            return LogoutSuccess();
          }),
          catchError(() => {
            this.clearLocalAuthentication();
            this.loaderService.hide();
            return of(LogoutSuccess());
          })
        );
      } else {
        this.loaderService.hide();
        return of(LogoutSuccess());
      }
    })
  ));

  tokenLogin$ = createEffect(() => this._actions$.pipe(
    ofType(TokenLogin),
    switchMap(({restore}) => {
      this.loaderService.show();
      // A page load that cannot pick its session back up simply lands on the sign-in page.
      const failed = (message: string) => LoginError({error: restore ? '' : message});
      return this.authenticationService.refreshSession().pipe(
        map((response: any) => {
          if (response.success) {
            return this.loginSuccess(response);
          }
          this.clearLocalAuthentication();
          this.loaderService.hide(true);
          return failed(this.loaderService.getErrorMessage(response, 'Invalid credentials'));
        }),
        catchError((error) => {
          this.clearLocalAuthentication();
          this.loaderService.hide(true);
          return of(failed(this.loaderService.getErrorMessage(error)));
        })
      );
    })
  ));

  oneClickLogin$ = createEffect(() => this._actions$.pipe(
    ofType(OneClickLogin),
    switchMap(({token}) => {
      this.loaderService.show();
      return this.authenticationService.oneClickLogin(token).pipe(
        map((response: any) => response.success
          ? this.loginSuccess(response)
          : this.loginFailure(response)),
        catchError((error) => of(this.loginFailure(error)))
      );
    })
  ));

  forgetPassword$ = createEffect(() => this._actions$.pipe(
    ofType(ForgetPassword),
    switchMap((action) => {
      this.loaderService.show();
      return this.authenticationService.password({
        email: action.email
      }).pipe(
        map((resp: any) => {
          if (resp.success) {
            this.loaderService.hide();
            return ForgetPasswordSuccess({forgetPassword: resp});
          }
          this.loaderService.hide(true);
          return ForgetPasswordError({error: this.loaderService.getErrorMessage(resp)});
        }),
        catchError(error => {
          this.loaderService.hide(true);
          return of(ForgetPasswordError({error: this.loaderService.getErrorMessage(error)}));
        })
      );
    })
  ));

  resetPassword$ = createEffect(() => this._actions$.pipe(
    ofType(ResetPassword),
    switchMap((action) => {
      this.loaderService.show();
      return this.authenticationService.resetPassword(action.password, action.token).pipe(
        map((resp: any) => {
          if (resp.success) {
            this.clearLocalAuthentication();
            this.loaderService.hide();
            return ResetPasswordSuccess({resetPassword: resp});
          }
          this.loaderService.hide(true);
          return ResetPasswordError({error: this.loaderService.getErrorMessage(resp)});
        }),
        catchError(error => {
          this.loaderService.hide(true);
          return of(ResetPasswordError({error: this.loaderService.getErrorMessage(error)}));
        })
      );
    })
  ));

  loginWithToken$ = createEffect(() => this._actions$.pipe(
    ofType(LoginUserWithId),
    switchMap((action) => {
      this.loaderService.show();
      return this.authenticationService.loginWithId(action.userId).pipe(
        map((response: any) => {
          if (response.success) {
            return this.loginSuccess(response, true);
          }
          this.loaderService.hide(true);
          return LoginUserWithIdError({error: this.loaderService.getErrorMessage(response, 'Invalid credentials')});
        }),
        catchError(error => {
          this.loaderService.hide(true);
          return of(LoginUserWithIdError({error: this.loaderService.getErrorMessage(error)}));
        })
      );
    })
  ));

  endImpersonation$ = createEffect(() => this._actions$.pipe(
    ofType(EndImpersonation),
    switchMap(() => {
      this.loaderService.show();
      return this.authenticationService.endImpersonation().pipe(
        map((response: any) => {
          if (response?.success && response?.token) {
            return this.loginSuccess(response);
          }
          this.loaderService.hide(true);
          return EndImpersonationError({
            error: this.loaderService.getErrorMessage(response, 'Could not return to your user')
          });
        }),
        catchError(error => {
          this.loaderService.hide(true);
          return of(EndImpersonationError({error: this.loaderService.getErrorMessage(error)}));
        })
      );
    })
  ));

  resetPasswordToken$ = createEffect(() => this._actions$.pipe(
    ofType(ResetPasswordToken),
    switchMap((action) => {
      this.loaderService.show();
      return this.authenticationService.resetPasswordToken(action.token).pipe(
        map((response: any) => {
          if (response.success) {
            this.loaderService.hide();
            return ResetPasswordTokenSuccess({
              passwordContext: response?.data?.password_context
                ? {
                  ...response.data.password_context,
                  reset_reason: response?.data?.reset_reason || null
                }
                : null
            });
          }
          this.loaderService.hide(true);
          return LoginError({error: this.loaderService.getErrorMessage(response, 'Invalid link')});
        }),
        catchError((error) => {
          this.loaderService.hide(true);
          return of(LoginError({error: this.loaderService.getErrorMessage(error)}));
        })
      );
    })
  ));

  completeLoginFlow$ = createEffect(() => this._actions$.pipe(
    ofType(CompleteLoginFlow),
    switchMap(({flow, code, recovery, method, rememberDevice, linkToken}) => {
      this.loaderService.show();
      let request: import('rxjs').Observable<any>;
      if (flow.code === 'AUTH_MFA_REQUIRED') {
        request = this.authenticationService.completeMfa(
          flow.challenge_token,
          code,
          recovery,
          method,
          rememberDevice,
          linkToken
        );
      } else if (flow.code === 'AUTH_SESSION_CONFLICT') {
        request = this.authenticationService.replaceSession(flow.conflict_token);
      } else {
        request = this.authenticationService.refreshSession();
      }
      return request.pipe(
        map((response: any) => {
          if (response.success && !Object.prototype.hasOwnProperty.call(
            response,
            'password_expiry_warning'
          ) && flow.password_expiry_warning) {
            response = {...response, password_expiry_warning: flow.password_expiry_warning};
          }
          return response.success ? this.loginSuccess(response) : this.loginFailure(response);
        }),
        catchError((error) => of(this.loginFailure(error)))
      );
    })
  ));
}
